import React, { useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { useForceChangeToNegativeStockState } from "../../../../../hooks/useForceChangeToNegativeStockState";
import { CancelOnDateOrderApi } from "../pages/Index";

interface Props {
	itemConfig: string | CancelOnDateOrderApi;
	onHide: () => void;
	actionOnSubmit: () => void;
}

interface FormProps {
	ordered_at: string;
}

const ConfirmOnDateOrderModalForm = ({ onHide, itemConfig, actionOnSubmit }: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const { addSuccessFlash } = useFlash();
	const form = useForm<FormProps>({
		criteriaMode: "all",
		defaultValues: {
			ordered_at: formatDateToString(new Date()),
		},
	});
	const doubleConfirmationActionHandler = useForceChangeToNegativeStockState();

	const {
		formState,
		formState: { errors },
		control,
		handleSubmit,
	} = form;

	const onSubmit: () => Promise<void> = handleSubmit(async (data: FormProps) => {
		setLoading(true);
		const isMultipleAction = typeof itemConfig === "object";
		const ids = isMultipleAction ? itemConfig.ids : itemConfig;
		const areAllItemsSelected = isMultipleAction ? itemConfig.areAllItemsSelected : false;

		const params: Record<string, any> =
			isMultipleAction && itemConfig.multipleActionsParams ? itemConfig.multipleActionsParams : {};
		params.ordered_at = data.ordered_at;
		await doubleConfirmationActionHandler(
			async (forceCloseWithNegativeStockState: boolean) => {
				const newParams = forceCloseWithNegativeStockState ? { ...params, force: true } : params;
				await apiOrganization.confirmOnDateOrder(ids, newParams, areAllItemsSelected);
				addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
				actionOnSubmit();
				onHide();
			},
			undefined,
			() => setLoading(false)
		);
	});
	return (
		<Modal onHide={onHide} show={itemConfig !== undefined}>
			<FormDirty formState={formState} onSubmit={onSubmit} key="confirm-on-date-modal-form">
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.order.header.confirm_on_date.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Alert variant="warning">{t("modules.order.field.confirm_on_date_alert.title")}</Alert>
					<FormDatePicker
						errors={errors}
						name="ordered_at"
						control={control}
						timePicker={false}
						label={t("modules.order.field.document_approval_date.title")}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default ConfirmOnDateOrderModalForm;
